import React, { useState } from 'react'

export interface InlineCodeSnippetProps {
  /** The code / DNS value users need to copy. */
  value: string
  /** Optional DOM id — useful when pairing with an external <label htmlFor=...>. */
  id?: string
  /** aria-label for the embedded copy button. Describe what gets copied. */
  copyLabel?: string
  /** Hide the copy button (pure read-only snippet). */
  hideCopy?: boolean
  className?: string
}

/**
 * InlineCodeSnippet -- Editorial Grid
 *
 * A light-surface, selectable single-line code snippet with a copy-to-clipboard
 * button on the right. Intended for DNS records, API keys, and other short
 * values a user must paste elsewhere.
 *
 * For multi-line / dark-theme code samples, use the legacy <CodeBlock> under
 * `src/components/` instead.
 */
export const InlineCodeSnippet: React.FC<InlineCodeSnippetProps> = ({
  value,
  id,
  copyLabel = 'Copy to clipboard',
  hideCopy = false,
  className = '',
}) => {
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value)
    } catch {
      const ta = document.createElement('textarea')
      ta.value = value
      document.body.appendChild(ta)
      ta.select()
      document.execCommand('copy')
      document.body.removeChild(ta)
    }
    setCopied(true)
    window.setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div
      className={[
        'flex items-stretch gap-2 w-full',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <code
        id={id}
        className={[
          'flex-1 min-w-0 select-all break-all',
          'bg-eg-surface border border-eg-border rounded-eg-md',
          'px-3 py-2 font-mono text-eg-sm text-eg-text',
        ].join(' ')}
      >
        {value}
      </code>
      {!hideCopy && (
        <button
          type="button"
          onClick={handleCopy}
          aria-label={copyLabel}
          aria-live="polite"
          className={[
            'flex-shrink-0 inline-flex items-center justify-center',
            'px-3 py-2 rounded-eg-md',
            'bg-eg-elevated border border-eg-border',
            'font-body text-eg-sm text-eg-text',
            'hover:bg-eg-bg transition-colors duration-150',
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-eg-primary focus-visible:ring-offset-1',
            'cursor-pointer',
          ].join(' ')}
        >
          {copied ? 'Copied!' : 'Copy'}
        </button>
      )}
    </div>
  )
}
